import { ActionIcon, Card, Checkbox, Group, Menu, Stack, Text, Tooltip } from '@mantine/core';
import { IconLink, IconRefresh } from '@tabler/icons-react';

import { useConversationAttachments } from '@/hooks/useConversationAttachments';
import { ConversationAttachment } from '@/types/attachment';
import { SourceSetSelection } from './ConversationView';
import { MessageAttachment } from './MessageAttachment';

interface ConversationFilesProps {
  threadId: string | undefined;
  /** Shared extraction source-set selection (analyst only). */
  selection?: SourceSetSelection;
  onToggleAttachment?: (attachmentId: string) => void;
  /** RR ids on the enquiry, offered as attach targets per file. */
  reportRequestIds?: string[];
  onAttachToRr?: (attachmentId: string, rrId: string) => Promise<void>;
}

/**
 * "Files in this conversation" — every attachment on the thread (incl.
 * pending uploads and unzipped archive members). Each file can be selected
 * into the extraction source set and/or attached to a report request.
 * Archive members are grouped under their parent zip.
 */
export function ConversationFiles({
  threadId,
  selection,
  onToggleAttachment,
  reportRequestIds,
  onAttachToRr,
}: Readonly<ConversationFilesProps>) {
  const { attachments, isLoading, isResyncing, resync } = useConversationAttachments(threadId);

  const topLevel = attachments.filter((a) => !a.parentAttachmentId);
  const childrenOf = (id: string) => attachments.filter((a) => a.parentAttachmentId === id);

  const renderRow = (att: ConversationAttachment, nested = false) => (
    <div key={att.id} style={{ marginLeft: nested ? 16 : 0 }}>
      <MessageAttachment
        attachment={att}
        compact
        leading={
          onToggleAttachment ? (
            <Checkbox
              size="xs"
              checked={selection?.attachmentIds.has(att.id) ?? false}
              onChange={() => onToggleAttachment(att.id)}
              aria-label={`Use ${att.filename} for extraction`}
            />
          ) : undefined
        }
        action={
          reportRequestIds && reportRequestIds.length > 0 && onAttachToRr ? (
            <Menu shadow="sm" width={260} position="bottom-end">
              <Menu.Target>
                <Group gap={2} style={{ cursor: 'pointer' }}>
                  <IconLink size={14} />
                  <Text size="xs">Attach to RR</Text>
                </Group>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Attach to report request</Menu.Label>
                {reportRequestIds.map((rrId) => (
                  <Menu.Item key={rrId} onClick={() => void onAttachToRr(att.id, rrId)}>
                    {rrId.slice(0, 8)}…
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ) : undefined
        }
      />
    </div>
  );

  return (
    <Card shadow="sm" padding="md" radius="md">
      <Group justify="space-between" align="center" mb="xs" wrap="nowrap">
        <Text fw={600}>Files in this conversation</Text>
        <Tooltip label="Re-sync files from the email thread" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => void resync()}
            loading={isResyncing}
            disabled={!threadId}
            aria-label="Re-sync files"
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
      {isLoading && attachments.length === 0 && (
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      )}
      {!isLoading && attachments.length === 0 && (
        <Text size="sm" c="dimmed">
          No files attached yet — try “Re-sync files” if the client emailed one.
        </Text>
      )}
      <Stack gap={6}>
        {topLevel.map((att) => (
          <Stack key={att.id} gap={4}>
            {renderRow(att)}
            {childrenOf(att.id).map((child) => renderRow(child, true))}
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}
